import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import { exportProfileCsv } from '../../utils/export';

type CategorySetRow = {
  id: string;
  user_id: string | null;
  name: string;
  categories: string[] | null;
  created_at: string | null;
  mode?: 'static' | 'dynamic';
  category_details?: { name: string; description?: string }[] | null;
  theme_description?: string | null;
};

type JobRow = { id: string; category_set_id: string | null; result: { text: string; category: string }[] | null };

const CategorySets: React.FC = () => {
  const [sets, setSets] = useState<CategorySetRow[] | null>(null);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // creator state
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMode, setNewMode] = useState<'static' | 'dynamic'>('static');
  const [newTheme, setNewTheme] = useState('');
  const [newDetails, setNewDetails] = useState<{ name: string; description?: string }[]>([{ name: '' }]);
  const [submitting, setSubmitting] = useState(false);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr) throw userErr;
      const userId = userData.user?.id;
      if (!userId) {
        setError('Please log in to view your category profiles.');
        setLoading(false);
        return;
      }

      // 1) Load profiles first
      const { data: setData, error: setErr } = await supabase
        .from('category_sets')
        .select('id, user_id, name, categories, created_at, mode, category_details, theme_description')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (setErr) throw setErr;
      setSets(setData || []);

      // 2) Load jobs for those profile IDs (if any)
      const ids = (setData || []).map((s: any) => s.id);
      if (ids.length === 0) {
        setJobs([]);
      } else {
        const { data: jobData, error: jobErr } = await supabase
          .from('jobs')
          .select('id, category_set_id, result')
          .in('category_set_id', ids);
        if (jobErr) {
          setJobs([]);
        } else {
          setJobs((jobData as any) || []);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load category profiles');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setIdToProcessed = useMemo(() => {
    const map = new Map<string, number>();
    for (const j of jobs) {
      if (!j.category_set_id) continue;
      const arr = Array.isArray(j.result) ? j.result : [];
      map.set(j.category_set_id, (map.get(j.category_set_id) || 0) + arr.length);
    }
    return map;
  }, [jobs]);

  const canSubmit = (() => {
    if (!newName.trim()) return false;
    if (newMode === 'static') return newDetails.filter(d => d.name.trim()).length >= 2;
    return newTheme.trim().length >= 10;
  })();

  const handleCreate = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Not authenticated. Please log in again.');

      const payload: any = {
        name: newName.trim(),
        mode: newMode,
        theme_description: newMode === 'dynamic' ? newTheme.trim() : newTheme.trim() || null,
        category_details: newMode === 'static' ? newDetails.filter(d => d.name.trim()) : null,
        categories: newMode === 'static' ? newDetails.filter(d => d.name.trim()).map(d => d.name.trim()) : [],
      };
      const { error } = await supabase
        .from('category_sets')
        .insert(payload)
        .select('id')
        .single();
      if (error) throw error;

      setCreating(false);
      setNewName('');
      setNewTheme('');
      setNewMode('static');
      setNewDetails([{ name: '' }]);
      await loadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create category profile');
      console.error('Create profile failed:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (profileId: string) => {
    const ok = window.confirm('Remove this category profile? This will delete related jobs.');
    if (!ok) return;
    try {
      await supabase.from('jobs').delete().eq('category_set_id', profileId);
      await supabase.from('category_sets').delete().eq('id', profileId);
      await loadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove profile');
    }
  };

  const handleExportProfile = async (profileId: string, mode: 'single' | 'onehot') => {
    const relatedJobs = jobs.filter(j => j.category_set_id === profileId);
    const allResults = relatedJobs.flatMap(j => j.result || []);
    const prof = (sets || []).find(s => s.id === profileId);
    const cats = prof?.categories || prof?.category_details?.map(d => d.name) || [];
    exportProfileCsv(profileId, allResults, cats, mode);
  };

  if (loading) return <div>Loading category profiles…</div>;
  if (error) return <div style={{ color: '#ff6b6b' }}>{error}</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Category Profiles</h3>
        <button className="button" onClick={() => setCreating(v => !v)}>{creating ? 'Close' : '+ New category profile'}</button>
      </div>
      <p style={{ color: 'var(--color-text-muted)', marginTop: 6 }}>
        Create and manage reusable category profiles to apply across your jobs.
      </p>

      {creating && (
        <div className="card" style={{ padding: 16, marginBottom: 12 }}>
          <h4 style={{ marginTop: 0 }}>Create category profile</h4>
          <div style={{ display: 'grid', gap: 10 }}>
            <div>
              <label className="label">Name</label>
              <input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="E.g. Support intents" />
            </div>
            <div>
              <label className="label">Mode</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className={`button ${newMode==='static' ? '' : 'secondary'}`} onClick={() => setNewMode('static')}>Static</button>
                <button type="button" className={`button ${newMode==='dynamic' ? '' : 'secondary'}`} onClick={() => setNewMode('dynamic')}>Dynamic</button>
              </div>
            </div>

            {newMode === 'static' && (
              <div style={{ display: 'grid', gap: 8 }}>
                <label className="label">Categories and optional descriptions</label>
                {newDetails.map((d, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
                    <input className="input" placeholder="Category name" value={d.name} onChange={(e) => {
                      const arr = [...newDetails];
                      arr[idx] = { ...arr[idx], name: e.target.value };
                      setNewDetails(arr);
                    }} />
                    <input className="input" placeholder="Optional description" value={d.description || ''} onChange={(e) => {
                      const arr = [...newDetails];
                      arr[idx] = { ...arr[idx], description: e.target.value };
                      setNewDetails(arr);
                    }} />
                    <button type="button" className="button secondary" onClick={() => setNewDetails(newDetails.filter((_, i) => i !== idx))}>Remove</button>
                  </div>
                ))}
                <div>
                  <button type="button" className="button" onClick={() => setNewDetails([...newDetails, { name: '' }])}>+ Add category</button>
                </div>
              </div>
            )}

            {newMode === 'dynamic' && (
              <div>
                <label className="label">What are categories about?</label>
                <textarea className="input" rows={4} placeholder="Describe the topic, e.g., Customer support intent categories" value={newTheme} onChange={(e) => setNewTheme(e.target.value)} />
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="button" disabled={!canSubmit || submitting} onClick={handleCreate}>{submitting ? 'Creating…' : 'Create profile'}</button>
              <button className="button secondary" onClick={() => setCreating(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#0f111a' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Name</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Mode</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Categories</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Processed items</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Creation date</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(sets || []).map(set => (
              <tr key={set.id}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{set.name}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{set.mode || 'static'}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{Array.isArray(set.categories) ? set.categories.length : (set.category_details || []).length}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{setIdToProcessed.get(set.id) || 0}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{set.created_at ? new Date(set.created_at).toLocaleString() : '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>
                  <button className="button secondary" onClick={() => window.location.href = `/app/profile/${set.id}`}>Dashboard</button>
                  <button className="button" style={{ marginLeft: 8 }} onClick={() => handleExportProfile(set.id, 'single')}>Export CSV</button>
                  <button className="button secondary" style={{ marginLeft: 8 }} onClick={() => handleExportProfile(set.id, 'onehot')}>Export one-hot</button>
                  <button className="button secondary" style={{ marginLeft: 8 }} onClick={() => handleRemove(set.id)}>Remove</button>
                </td>
              </tr>
            ))}
            {(!sets || sets.length === 0) && (
              <tr>
                <td colSpan={6} style={{ padding: '12px', textAlign: 'center', color: 'var(--color-text-muted)' }}>No category profiles yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CategorySets; 